import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, shareReplay, switchMap, tap, throwError } from 'rxjs';
import {
  AsmResolvedDefinition,
  AsmResolvedEntry,
  AsmResolvedSettings,
  AsmResolvedValue,
  AsmSettingContextId,
  AsmSettingDefinition,
  AsmSettingDefinitionContext,
  AsmSettingPayload,
  AsmSettingPayloadRow,
  AsmSettingRefusal,
  AsmSettingRefusalReasonId,
  AsmSettingRule,
  AsmSettingRuleDraft,
  AsmSettingTarget,
  AsmSettingValue,
} from '@asm/components-core/models/asm-setting';
import {
  ObjectTools,
  SolFieldAccess,
  SolFieldDescriptor,
  SolFieldKeyword,
  SolFieldValidator,
  SolPage,
  SolText,
  TypeService,
  ViewService,
} from '@sol/components';
import { AbstractAsmService } from '@asm/components-core/services/abstract-asm.service';
import { SessionService } from './session.service';
import { AsmContextTools } from '../tools/asm-context-tools';

export interface AsmSettingKeywordDto {
  id: string;
  label: string;
  description: string | null;
}

export interface AsmSettingValidatorDto {
  type: string;
  params: Record<string, unknown>;
}

export interface AsmSettingFieldDto {
  name: string;
  label: string;
  value_type: string;
  view_id: string | null;
  description: string | null;
  required: boolean;
  keywords: AsmSettingKeywordDto[];
  validators: AsmSettingValidatorDto[];
}

export interface AsmSettingDefinitionContextDto {
  context: string;
  manage_permission: string;
  i_may_write: boolean;
}

export interface AsmSettingDefinitionDto {
  id: string;
  display_name: string;
  description: string;
  entry_key: string | null;
  contexts: AsmSettingDefinitionContextDto[];
  fields: AsmSettingFieldDto[];
}

interface AsmSettingRuleDto {
  id: number;
  definition: string;
  context: string;
  target: string | null;
  target_label: string;
  payload: AsmSettingPayload;
  i_may_write: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

interface AsmSettingRulePageDto {
  items: AsmSettingRuleDto[];
  page: SolPage;
}

interface AsmSettingTargetDto {
  id: string | null;
  label: string;
  already_set: boolean;
  hint: string | null;
}

export interface AsmSettingRefusalDto {
  reason: string;
  context: string | null;
}

export interface AsmResolvedValueDto {
  value: AsmSettingValue;
  context: string | null;
  editable: boolean;
  refusal: AsmSettingRefusalDto | null;
}

export type AsmResolvedEntryDto = AsmSettingPayloadRow & {
  key: string;
  context: string | null;
  editable: boolean;
  refusal: AsmSettingRefusalDto | null;
};

export type AsmResolvedDefinitionDto = Record<string, AsmResolvedValueDto> | { entries: AsmResolvedEntryDto[] };

export type AsmResolvedSettingsDto = Record<string, AsmResolvedDefinitionDto>;

export interface AsmSettingRuleFilter {
  definition?: string;
  context?: string;
  target?: string;
  q?: readonly string[];
  self_service?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface AsmSettingRulePage {
  items: AsmSettingRule[];
  page: SolPage;
}

export interface AsmSettingsAdminServiceState {
  readonly definitions: readonly AsmSettingDefinition[];
  readonly rules: readonly AsmSettingRule[];
}

/** The whole `/settings` surface: the catalogue every generated form reads, the rules, and the target picker. */
@Injectable({
  providedIn: 'root',
})
export class AsmSettingsAdminService extends AbstractAsmService<AsmSettingsAdminServiceState> {
  private static readonly RESOLUTION_KEYS = ['context', 'editable', 'refusal', 'key'];

  // Dependencies
  private readonly sessionService = inject(SessionService);
  private readonly typeService = inject(TypeService);
  private readonly viewService = inject(ViewService);

  // Cache
  private definitions?: { params: Readonly<Record<string, string>>; value: Observable<AsmSettingDefinition[]> };

  constructor() {
    super('/api/v3/settings', { definitions: [], rules: [] });
  }

  public whenDefinitionsFetched(): Observable<AsmSettingDefinition[]> {
    return this.sessionService.whenFetched().pipe(switchMap(() => this.fetchDefinitions(this.contextParams())));
  }

  public fetchRules(filter: AsmSettingRuleFilter = {}): Observable<AsmSettingRulePage> {
    return this.httpGet<AsmSettingRulePageDto>('/rules', { params: { ...filter, ...this.contextParams() } }).pipe(
      map(dto => AsmSettingsAdminService.toRulePage(dto)),
      tap(page => this.patch({ rules: page.items })),
    );
  }

  public fetchTargets(definition: string, context: string, query?: string): Observable<AsmSettingTarget[]> {
    return this.httpGet<AsmSettingTargetDto[]>('/targets', {
      params: { definition, context, q: query, ...this.contextParams() },
    }).pipe(
      map(dtos =>
        dtos.map(dto => ({
          id: dto.id ?? undefined,
          label: dto.label,
          hint: dto.hint ?? undefined,
          alreadySet: dto.already_set,
        })),
      ),
    );
  }

  public createRules(draft: AsmSettingRuleDraft): Observable<void> {
    return this.httpPost<AsmSettingRuleDraft, void>('/rules', draft, { params: this.contextParams() });
  }

  public updateRule(id: number, payload: AsmSettingPayload): Observable<void> {
    return this.httpPut<{ payload: AsmSettingPayload }, void>(
      `/rules/${id}`,
      { payload },
      { params: this.contextParams() },
    );
  }

  public deleteRule(id: number): Observable<void> {
    return this.httpDelete<void>(`/rules/${id}`, { params: this.contextParams() });
  }

  private fetchDefinitions(params: Readonly<Record<string, string>>): Observable<AsmSettingDefinition[]> {
    if (this.definitions === undefined || !ObjectTools.equals(this.definitions.params, params)) {
      const value = this.httpGet<AsmSettingDefinitionDto[]>('/definitions', { params }).pipe(
        map(dtos => dtos.map(dto => this.toDefinition(dto))),
        tap(definitions => this.patch({ definitions })),
        catchError((error: unknown) => {
          if (this.definitions?.value === value) {
            this.definitions = undefined;
          }
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.definitions = { params, value };
    }
    return this.definitions.value;
  }

  private contextParams(): Record<string, string> {
    return AsmContextTools.contextParams(this.sessionService.state, this.asmComponentsService.state);
  }

  private toDefinition(dto: AsmSettingDefinitionDto): AsmSettingDefinition {
    return {
      id: dto.id,
      displayName: dto.display_name,
      description: dto.description,
      entryKey: dto.entry_key ?? undefined,
      contexts: dto.contexts.map(context => AsmSettingsAdminService.toDefinitionContext(context)),
      fields: dto.fields.map(field => this.toField(field)),
    };
  }

  private static toDefinitionContext(dto: AsmSettingDefinitionContextDto): AsmSettingDefinitionContext {
    return {
      context: dto.context as AsmSettingContextId,
      managePermission: dto.manage_permission,
      iMayWrite: dto.i_may_write,
    };
  }

  /**
   * Settings labels are introspected from Django model fields, so they are literal text no bundle
   * holds -- hence `translatable: false`. A column is closed to its keyword catalogue when it
   * declares one, and no settings column is a secret.
   */
  private toField(dto: AsmSettingFieldDto): SolFieldDescriptor {
    return {
      name: dto.name,
      label: dto.label,
      translatable: false,
      type: this.typeService.resolveType(dto.value_type),
      viewId: dto.view_id === null ? undefined : this.viewService.resolveViewId(dto.view_id),
      description: dto.description ?? undefined,
      required: dto.required,
      keywords: dto.keywords.map(keyword => AsmSettingsAdminService.toKeyword(keyword)),
      editable: dto.keywords.length === 0,
      validators: dto.validators.map(validator => AsmSettingsAdminService.toValidator(validator)),
      access: SolFieldAccess.readWrite,
    };
  }

  private static toKeyword(dto: AsmSettingKeywordDto): SolFieldKeyword {
    return {
      id: dto.id,
      label: SolText.ofRaw(dto.label),
      description: dto.description === null ? undefined : SolText.ofRaw(dto.description),
    };
  }

  private static toValidator(dto: AsmSettingValidatorDto): SolFieldValidator {
    return { type: dto.type, params: dto.params };
  }

  private static toRulePage(dto: AsmSettingRulePageDto): AsmSettingRulePage {
    return { items: dto.items.map(item => AsmSettingsAdminService.toRule(item)), page: dto.page };
  }

  private static toRule(dto: AsmSettingRuleDto): AsmSettingRule {
    return {
      id: dto.id,
      definition: dto.definition,
      context: dto.context as AsmSettingContextId,
      target: dto.target ?? undefined,
      targetLabel: dto.target_label,
      payload: dto.payload,
      iMayWrite: dto.i_may_write,
      updatedBy: dto.updated_by ?? undefined,
      updatedAt: dto.updated_at === null ? undefined : new Date(dto.updated_at),
    };
  }

  public static toResolvedSettings(dto: AsmResolvedSettingsDto): AsmResolvedSettings {
    const resolved: AsmResolvedSettings = {};
    for (const [definition, payload] of Object.entries(dto)) {
      resolved[definition] = AsmSettingsAdminService.toResolvedDefinition(payload);
    }
    return resolved;
  }

  private static toResolvedDefinition(dto: AsmResolvedDefinitionDto): AsmResolvedDefinition {
    if (AsmSettingsAdminService.hasEntries(dto)) {
      return { fields: {}, entries: dto.entries.map(entry => AsmSettingsAdminService.toResolvedEntry(entry)) };
    }
    const fields: Record<string, AsmResolvedValue> = {};
    for (const [name, value] of Object.entries(dto)) {
      fields[name] = AsmSettingsAdminService.toResolvedValue(value);
    }
    return { fields, entries: [] };
  }

  private static hasEntries(dto: AsmResolvedDefinitionDto): dto is { entries: AsmResolvedEntryDto[] } {
    return Array.isArray((dto as { entries?: unknown }).entries);
  }

  private static toResolvedValue(dto: AsmResolvedValueDto): AsmResolvedValue {
    return {
      value: dto.value,
      context: dto.context === null ? undefined : (dto.context as AsmSettingContextId),
      editable: dto.editable,
      refusal: AsmSettingsAdminService.toRefusal(dto.refusal),
    };
  }

  private static toResolvedEntry(dto: AsmResolvedEntryDto): AsmResolvedEntry {
    const values: AsmSettingPayloadRow = {};
    for (const [name, value] of Object.entries(dto)) {
      if (!AsmSettingsAdminService.RESOLUTION_KEYS.includes(name)) {
        values[name] = value as AsmSettingValue;
      }
    }
    return {
      values,
      context: dto.context === null ? undefined : (dto.context as AsmSettingContextId),
      editable: dto.editable,
      refusal: AsmSettingsAdminService.toRefusal(dto.refusal),
    };
  }

  private static toRefusal(dto: AsmSettingRefusalDto | null): AsmSettingRefusal | undefined {
    if (dto === null) {
      return undefined;
    }
    return {
      reason: dto.reason as AsmSettingRefusalReasonId,
      context: dto.context === null ? undefined : (dto.context as AsmSettingContextId),
    };
  }
}
